import { cn } from '@portals-platform/ui';
import { AlertCircle } from 'lucide-react';
import type * as React from 'react';
import { FieldProvider, useFieldIds } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';
import { type ColSpan, colSpanClass } from './form-grid.js';

interface FormFieldProps {
  name: string;
  label: string;
  required?: boolean | undefined;
  optional?: boolean | undefined;
  hint?: string | undefined;
  col?: ColSpan | undefined;
  /**
   * Render the label as a `<legend>` inside a `<fieldset>`. Required for controls made of
   * several inputs — radio groups, segmented cards, pill toggles — where a `<label for>`
   * has no single control to point at.
   */
  asFieldset?: boolean | undefined;
  children: React.ReactNode;
  className?: string | undefined;
}

export function FormField({ name, hint, col = 6, className, ...rest }: FormFieldProps) {
  return (
    <FieldProvider name={name} hasHint={!!hint}>
      <div className={cn('flex flex-col gap-2', colSpanClass(col), className)}>
        <FormFieldInner name={name} hint={hint} {...rest} />
      </div>
    </FieldProvider>
  );
}

function FormFieldInner({
  name,
  label,
  required,
  optional,
  hint,
  asFieldset,
  children,
}: Omit<FormFieldProps, 'col' | 'className'>) {
  const error = useFieldError(name);
  const ids = useFieldIds();

  // Micro-label: uppercase and wide tracking always travel together.
  const labelClasses =
    'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground';

  const labelContent = (
    <>
      {label}
      {required && (
        <span className="text-destructive" aria-hidden="true">
          *
        </span>
      )}
      {required && <span className="sr-only">(required)</span>}
      {optional && (
        <span className="ml-auto text-[11px] font-medium normal-case tracking-normal text-muted-foreground/70">
          Optional
        </span>
      )}
    </>
  );

  const message = error ? (
    <p
      id={ids?.errorId}
      // role="alert" so the message is announced when it appears, not only when the
      // control is next focused.
      role="alert"
      className="flex items-center gap-1.5 text-[11px] font-medium text-destructive"
    >
      <AlertCircle className="size-3 shrink-0" aria-hidden="true" />
      {error.message}
    </p>
  ) : hint ? (
    <p id={ids?.hintId} className="text-[11px] leading-relaxed text-muted-foreground">
      {hint}
    </p>
  ) : null;

  if (asFieldset) {
    return (
      <fieldset className="flex flex-col gap-2 border-0 p-0 m-0 min-w-0">
        <legend className={cn(labelClasses, 'p-0')}>{labelContent}</legend>
        {children}
        {message}
      </fieldset>
    );
  }

  return (
    <>
      <label htmlFor={ids?.controlId ?? name} className={labelClasses}>
        {labelContent}
      </label>
      {children}
      {message}
    </>
  );
}
